import { useOrganizationSuspenseQuery } from "@/api/queries/organizations/useOrganizationSuspenseQuery"
import { Project } from "@/api/types/projects"
import { router } from "@/main"
import { checkPermissions } from "@/utils/checkPermissions"
import { ChevronRightIcon } from "@heroicons/react/24/solid"
import { createFileRoute, useParams } from "@tanstack/react-router"
import { useCallback } from "react"
import {
  EmptyState,
  formatProjectDuration,
  formatProjectTargetGroup,
  PageHeader,
  projectTypeOptions,
  Separator,
} from "toptaal-shared"

export const Route = createFileRoute(
  "/beheer/organisaties/$organizationId/contracten/",
)({
  component: OrganizationOverview,
  beforeLoad: () => checkPermissions("read", "organizations"),
})

function OrganizationOverview() {
  const { organizationId } = useParams({
    from: "/beheer/organisaties/$organizationId/contracten/",
  })

  const { data: organization } = useOrganizationSuspenseQuery({
    organizationId: Number(organizationId),
  })

  const projectType = useCallback((type: Project["type"]) => {
    return (
      projectTypeOptions.find((option) => option.value === type)?.label ||
      "Onbekend"
    )
  }, [])

  const handleNavigateToProject = useCallback((contractId: Project["id"]) => {
    router.navigate({
      to: "/beheer/contracten/$contractId/notities",
      params: { contractId: String(contractId) },
    })
  }, [])

  return (
    <div>
      <PageHeader className="mb-4">Contracten</PageHeader>
      <div className="flex flex-col gap-2 overflow-y-auto">
        {organization.projects?.length ? (
          organization.projects.map((project) => (
            <div
              key={project.id}
              className="p-3 bg-primary-light-blue rounded relative cursor-pointer"
              onClick={() => handleNavigateToProject(project.id)}
            >
              <p className="font-semibold mb-2">{project.name}</p>

              <div className="flex items-center gap-3">
                <p className="text-sm">{projectType(project.type)}</p>
                <Separator orientation="vertical" />
                <p className="text-sm">
                  {formatProjectTargetGroup(project.targetGroup)}
                </p>
                <Separator orientation="vertical" />
                <p className="text-sm">
                  {formatProjectDuration(project.startDate, project.endDate)}
                </p>
              </div>

              <ChevronRightIcon className="size-5 text-primary-dark-blue absolute right-4 top-1/2 transform -translate-y-1/2" />
            </div>
          ))
        ) : (
          <EmptyState className="text-sm">Geen contracten gevonden.</EmptyState>
        )}
      </div>
    </div>
  )
}
